import React from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { ArrowRight, Edit3, MessageSquare } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import * as P from '@/lib/portal/service';

// What the client must act on: variation issues awaiting decision + RFIs assigned to them. Counts come from existing tables (RLS-scoped).
export function useDecisions(projectId, role) {
  const { user } = useAuth();
  const iss = useSWR(projectId ? ['cv-issues', projectId] : null, () => P.issues(projectId), { shouldRetryOnError: false });
  const rf = useSWR(projectId ? ['rfis', projectId] : null, () => P.rfis(projectId));
  const variations = role === 'client' ? (iss.data || []).filter((i) => ['issued', 'clarification_requested'].includes(i.status)) : [];
  const rfis = (rf.data || []).filter((r) => r.status === 'open' && r.assigned_to === user?.id);
  return { variations, rfis, total: variations.length + rfis.length, loading: iss.isLoading || rf.isLoading };
}

export default function DecisionsRail({ projectId, role, compact }) {
  const d = useDecisions(projectId, role);
  if (!d.total) return null;
  const items = [
    ...d.variations.map((v) => ({ id: `v-${v.id}`, icon: Edit3, title: v.reference ? `${v.reference} — ${v.description || 'Variation'}` : (v.description || 'Variation'), sub: v.status === 'clarification_requested' ? 'Clarification requested' : `Rev ${v.revision_no} · awaiting your decision`, to: `/portal/projects/${projectId}?tab=variations` })),
    ...d.rfis.map((r) => ({ id: `r-${r.id}`, icon: MessageSquare, title: r.subject || `RFI ${r.rfi_no}`, sub: 'Response requested from you', to: `/portal/projects/${projectId}?tab=rfis` })),
  ];
  return (
    <section className="rounded-2xl bg-[#3A3936] text-[#F7F5F1] px-6 py-5 md:px-8" data-testid="decisions-rail">
      <div className="flex items-baseline justify-between gap-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#957C5D] font-semibold">Your decisions</div>
        <div className="text-xs text-[#B5AFA5]">{d.total} item{d.total > 1 ? 's' : ''} awaiting you</div>
      </div>
      <ul className={`mt-3 divide-y divide-white/10 ${compact ? '' : 'md:grid md:grid-cols-2 md:gap-x-8 md:divide-y-0'}`}>
        {items.slice(0, compact ? 3 : 6).map((it) => (
          <li key={it.id}><Link to={it.to} data-testid={`decision-${it.id}`} className="group flex items-center gap-3 py-3">
            <span className="h-9 w-9 rounded-full bg-[#957C5D]/20 text-[#D8D0C4] flex items-center justify-center shrink-0"><it.icon size={15} /></span>
            <span className="min-w-0 flex-1"><span className="block text-sm font-medium truncate">{it.title}</span><span className="block text-xs text-[#B5AFA5]">{it.sub}</span></span>
            <ArrowRight size={15} className="text-[#957C5D] transition-transform duration-200 group-hover:translate-x-1" />
          </Link></li>
        ))}
      </ul>
    </section>
  );
}
